import { createContext, useContext, useEffect, useState } from 'react';

// Define our breakpoints
export const BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
  desktop: 1280,
  large: 1536,  // New breakpoint for large screens
} as const;

type Breakpoint = 'mobile' | 'tablet' | 'desktop' | 'large';

interface BreakpointContextType {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isLarge: boolean;  // Add new property
  breakpoint: Breakpoint;
  width: number;
}

const BreakpointContext = createContext<BreakpointContextType | undefined>(undefined);

export function BreakpointProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<BreakpointContextType>({
    isMobile: false,
    isTablet: false,
    isDesktop: false,
    isLarge: false,  // Add new property
    breakpoint: 'desktop',
    width: typeof window !== 'undefined' ? window.innerWidth : 0
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setState({
        isMobile: width < BREAKPOINTS.mobile,
        isTablet: width >= BREAKPOINTS.mobile && width < BREAKPOINTS.tablet,
        isDesktop: width >= BREAKPOINTS.tablet && width < BREAKPOINTS.large,
        isLarge: width >= BREAKPOINTS.large,
        breakpoint: width < BREAKPOINTS.mobile 
          ? 'mobile' 
          : width < BREAKPOINTS.tablet 
          ? 'tablet' 
          : width < BREAKPOINTS.large
          ? 'desktop'
          : 'large',
        width
      });
    };

    // Initial check
    handleResize();

    // Add event listener with debounce
    let timeoutId: NodeJS.Timeout;
    const debouncedResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 100);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', debouncedResize);
    };
  }, []);

  return (
    <BreakpointContext.Provider value={state}>
      {children}
    </BreakpointContext.Provider>
  );
}

export function useBreakpoint() {
  const context = useContext(BreakpointContext);
  if (context === undefined) {
    throw new Error('useBreakpoint must be used within a BreakpointProvider');
  }
  return context;
} 